import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import Layout from '../../../../component/layout';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { getItem } from '../../../../../../utils/localStorage';

function ViewPrepaidPowerCustomer() {
    const navigate = useNavigate();
    const { accountId } = useParams();
    const location = useLocation();
    const toast = useRef(null);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const defaultCustomerData = {
        name: 'N/A',
        id: 'N/A',
        accountNumber: 'N/A',
        deviceId: 'N/A',
        status: 'N/A',
        phone: 'N/A',
        userType: 'Prepaid',
        updatedAt: 'N/A',
        meter_id: 'N/A',
        unitName: 'N/A',
        email: 'N/A'
    };

    const [customerData, setCustomerData] = useState(defaultCustomerData);
    const [prepaidBalance, setPrepaidBalance] = useState(0.0);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [balanceLoading, setBalanceLoading] = useState(false);

    const [isNinja, setIsNinja] = useState(false);
    const [totalCredits, setTotalCredits] = useState(0);
    const [totalDebits, setTotalDebits] = useState(0);
    const [credits, setCredits] = useState([]);
    const [debits, setDebits] = useState([]);
    const [creditTotalRecords, setCreditTotalRecords] = useState(0);
    const [debitTotalRecords, setDebitTotalRecords] = useState(0);
    const [creditLazyParams, setCreditLazyParams] = useState({
        first: 0,
        rows: 5,
        page: 0,
    });
    const [debitLazyParams, setDebitLazyParams] = useState({
        first: 0,
        rows: 5,
        page: 0,
    });

    const [addCreditDialog, setAddCreditDialog] = useState(false);
    const [newCreditAmount, setNewCreditAmount] = useState(null);
    const [newCreditReason, setNewCreditReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Company details for the statement
    const companyDetails = {
        name: 'PayServe Ltd',
        address: 'Nairobi, Kenya',
        country: 'Kenya',
        phone: '+254 709 000 505',
        email: 'info@payserve.co.ke'
    };

    // FETCH ALL DATA IMMEDIATELY WHEN COMPONENT MOUNTS
    useEffect(() => {
        if (accountId && selectedFacilityId) {
            fetchAllCustomerData(accountId);
        }
    }, [accountId, selectedFacilityId]);

    // Audit logs are only visible to ninja users
    useEffect(() => {
        (async () => {
            const appUser = await getItem('APPUSER');
            setIsNinja(appUser?.user?.role === 'ninja');
        })();
    }, []);

    // Refetch credits and debits when pagination changes
    useEffect(() => {
        if (accountId && selectedFacilityId && (creditLazyParams.first > 0 || debitLazyParams.first > 0)) {
            fetchCreditsAndDebits(accountId);
        }
    }, [creditLazyParams, debitLazyParams, accountId, selectedFacilityId]);

    // PARALLEL FETCH OF ALL DATA
    const fetchAllCustomerData = async (id) => {
        try {
            setLoading(true);
            setBalanceLoading(true);

            // Fetch all data in parallel
            const [
                customerResponse,
                balanceResponse,
                creditsResponse,
                debitsResponse
            ] = await Promise.allSettled([
                makeRequest2(`/api/app/power_meter_management/controllers/customer_management/get_customer_account/${id}`, 'GET'),
                makeRequest2(`/api/app/power_meter_management/controllers/customer_management/get_customer_account_balance/${id}`, 'GET'),
                makeRequest2(`/api/app/power_meter_management/controllers/customer_management/get_customer_credits/${id}?facilityId=${selectedFacilityId}`, 'GET'),
                makeRequest2(`/api/app/power_meter_management/controllers/customer_management/get_customer_debits/${id}`, 'GET')
            ]);

            // Handle customer account data
            if (customerResponse.status === 'fulfilled' && customerResponse.value.success) {
                const accountData = customerResponse.value.data.data || customerResponse.value.data;
                const customerDetails = {
                    id: accountData._id || accountData.id,
                    name: accountData.customerName || 'N/A',
                    accountNumber: accountData.account_no || 'N/A',
                    deviceId: accountData.deviceId || 'N/A',
                    status: accountData.status || 'N/A',
                    phone: accountData.phoneNumber || 'N/A',
                    email: accountData.email || 'N/A',
                    userType: accountData.payment_type || 'Unknown',
                    updatedAt: formatDateTime(accountData.updatedAt),
                    meter_id: accountData.meterId || 'N/A',
                    unitName: accountData.unitInfo?.unitName || 'N/A'
                };
                setCustomerData(customerDetails);
            } else {
                console.error('Customer account fetch failed:', customerResponse.reason || customerResponse.value);
                toastify('Error fetching customer data', 'error');
            }

            // Handle balance data
            if (balanceResponse.status === 'fulfilled' && balanceResponse.value.success) {
                const balanceData = balanceResponse.value.data.data || balanceResponse.value.data || {};
                setPrepaidBalance(balanceData.currentBalance || 0.0);
            } else {
                setPrepaidBalance(0.0);
                // Don't show error toast for balance as it might not be critical
            }

            // Handle credits data
            if (creditsResponse.status === 'fulfilled' && creditsResponse.value.success) {
                const creditsData = creditsResponse.value.data.data?.credits || creditsResponse.value.data.data || [];
                setCredits(creditsData);
                setTotalCredits(creditsResponse.value.data?.data?.totalCreditAmount || 0);
                setCreditTotalRecords(creditsResponse.value.data?.totalCount || creditsData.length);
            } else {
                console.error('Credits fetch failed:', creditsResponse.reason || creditsResponse.value);
                setCredits([]);
                setTotalCredits(0);
                setCreditTotalRecords(0);
            }

            // Handle debits data
            if (debitsResponse.status === 'fulfilled' && debitsResponse.value.success) {
                const debitsData = debitsResponse.value.data.data?.debits || debitsResponse.value.data.data || [];
                setDebits(debitsData);
                setTotalDebits(debitsResponse.value.data?.data?.totalDebitAmount || 0);
                setDebitTotalRecords(debitsResponse.value.data?.totalCount || debitsData.length);
            } else {
                console.error('Debits fetch failed:', debitsResponse.reason || debitsResponse.value);
                setDebits([]);
                setDebitTotalRecords(0);
            }

        } catch (error) {
            console.error('Error fetching customer data:', error);
            toastify('Error loading customer information', 'error');
        } finally {
            setLoading(false);
            setBalanceLoading(false);
        }
    };

    // SEPARATE FUNCTION FOR PAGINATION REFETCH (CREDITS & DEBITS ONLY)
    const fetchCreditsAndDebits = async (id) => {
        try {
            setLoading(true);

            const [creditsResponse, debitsResponse] = await Promise.allSettled([
                makeRequest2(`/api/app/power_meter_management/controllers/customer_management/get_customer_credits/${id}?facilityId=${selectedFacilityId}`, 'GET'),
                makeRequest2(`/api/app/power_meter_management/controllers/customer_management/get_customer_debits/${id}`, 'GET')
            ]);

            // Handle credits
            if (creditsResponse.status === 'fulfilled' && creditsResponse.value.success) {
                const creditsData = creditsResponse.value.data.data?.credits || creditsResponse.value.data.data || [];
                setCredits(creditsData);
                setTotalCredits(creditsResponse.value.data?.data?.totalCreditAmount || 0);
                setCreditTotalRecords(creditsResponse.value.data?.totalCount || creditsData.length);
            }

            // Handle debits
            if (debitsResponse.status === 'fulfilled' && debitsResponse.value.success) {
                const debitsData = debitsResponse.value.data.data?.debits || debitsResponse.value.data.data || [];
                setDebits(debitsData);
                setTotalDebits(debitsResponse.value.data?.data?.totalDebitAmount || 0);
                setDebitTotalRecords(debitsResponse.value.data?.totalCount || debitsData.length);
            }

        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    // REFRESH BALANCE ONLY
    const handleRefreshBalance = async () => {
        if (!accountId) return;

        try {
            setBalanceLoading(true);
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/get_customer_account_balance/${accountId}`,
                'GET'
            );

            if (response.success) {
                // Fix: Access response.data.data instead of just response.data
                const balanceData = response.data.data || response.data || {};

                // Handle the correct response structure: data.data.currentBalance
                setPrepaidBalance(balanceData.currentBalance || balanceData.currentBalance || balanceData.currentBalance || 0.0);
                toastify('Balance refreshed successfully', 'success');
            } else {
                toastify('Failed to refresh balance', 'error');
            }
        } catch (error) {
            console.error('Error refreshing balance:', error);
            toastify('Error refreshing balance', 'error');
        } finally {
            setBalanceLoading(false);
        }
    };

    const onCreditPage = (event) => {
        setCreditLazyParams(event);
    };

    const onDebitPage = (event) => {
        setDebitLazyParams(event);
    };

    // Helpers for table formatting
    const formatNumber = (value) => {
        return new Intl.NumberFormat('en-KE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const amountBodyTemplate = (rowData) => {
        return `KES ${formatNumber(rowData.amount || 0)}`;
    };

    const totalAmountBodyTemplate = (rowData) => {
        return `KES ${formatNumber(rowData.totalAmount || 0)}`;
    };

    const consumptionBodyTemplate = (rowData) => {
        const formattedConsumption = formatNumber(rowData.consumption || 0);
        return `${formattedConsumption} kWh`;
    };

    const readingsBodyTemplate = (rowData) => {
        return `${rowData.prev_reading || 0} → ${rowData.current_reading || 0}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString || dateString === 'N/A') return 'N/A';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'N/A';
        }
    };

    const exportCreditsCSV = () => {
        const header = [
            'Reference', 'Amount (KES)', 'Time', 'Date Added', 'Payment Type', 'Reason'
        ];

        const data = credits.map(credit => [
            credit.ref,
            credit.amount || 0,
            credit.time,
            credit.addedOn,
            credit.type,
            credit.reason || 'N/A'
        ]);

        downloadCSV(data, header, `Power_Credits_${customerData.accountNumber}`);
    };

    const exportDebitsCSV = () => {
        const header = [
            'Previous Reading', 'Current Reading', 'Consumption (kWh)', 'Rate',
            'Total Amount (KES)', 'Date', 'Time'
        ];

        const data = debits.map(debit => [
            debit.prev_reading || 0,
            debit.current_reading || 0,
            debit.consumption || 0,
            debit.rate,
            debit.totalAmount || 0,
            debit.date,
            debit.time
        ]);

        downloadCSV(data, header, `Power_Debits_${customerData.accountNumber}`);
    };

    const downloadCSV = (data, header, filename) => {
        const csvContent = [
            header.join(','),
            ...data.map(row => row.map(cell =>
                typeof cell === 'string' && cell.includes(',')
                    ? `"${cell}"`
                    : cell
            ).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute(
            'download',
            `${filename}_${new Date().toISOString().slice(0, 10)}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Data exported to CSV', life: 3000 });
    };

    // Handle navigation back to the meter view page with meterId
    const handleNavigateBack = () => {
        navigate(-1);
    };

    const openAddDialog = () => {
        setNewCreditAmount(null);
        setNewCreditReason('');
        setAddCreditDialog(true);
    };

    const closeAddDialog = () => {
        setAddCreditDialog(false);
        setNewCreditAmount(null);
        setNewCreditReason('');
    };

    const generateReferenceNumber = () => {
        const randomDigits = Math.floor(100000 + Math.random() * 900000);
        return `POWER-${randomDigits}`;
    };

    const handleAddCredit = async () => {
        // Validation
        if (!newCreditAmount || newCreditAmount <= 0) {
            toastify('Please enter a valid amount', 'error');
            return;
        }

        if (!newCreditReason || newCreditReason.trim() === '') {
            toastify('Please provide a reason for adding credit', 'error');
            return;
        }

        try {
            setSubmitting(true);

            const now = new Date();
            const creditData = {
                accountNo: customerData.accountNumber,
                amount: newCreditAmount,
                ref: generateReferenceNumber(),
                type: 'Manual',
                addedOn: now.toISOString().split('T')[0],
                time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
                reason: newCreditReason.trim()
            };

            // Use accountId for consistency
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/add_credit_to_customer/${accountId}`,
                'POST',
                creditData
            );

            if (response.success) {
                toastify('Credit added successfully', 'success');
                closeAddDialog();
                // Refresh credits and balance
                fetchCreditsAndDebits(accountId);
                handleRefreshBalance();
            } else {
                toastify(response.error || 'Failed to add credit', 'error');
            }
        } catch (error) {
            console.error('Error adding credit:', error);
            toastify('Error adding credit', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const renderAddCreditDialog = () => {
        return (
            <Dialog
                header="Add Manual Credit"
                visible={addCreditDialog}
                style={{ width: '450px' }}
                onHide={closeAddDialog}
                modal
                className="p-fluid"
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={closeAddDialog}
                            disabled={submitting}
                        />
                        <Button
                            label="Add Credit"
                            icon="pi pi-check"
                            className="p-button-primary"
                            onClick={handleAddCredit}
                            loading={submitting}
                        />
                    </div>
                }
            >
                <div className="field">
                    <label htmlFor="amount">Amount (KES) <span className="text-danger">*</span></label>
                    <InputNumber
                        id="amount"
                        value={newCreditAmount}
                        onValueChange={(e) => setNewCreditAmount(e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        placeholder="Enter amount"
                        className="w-100"
                        min={0}
                    />
                </div>

                <div className="field">
                    <label htmlFor="reason">Reason <span className="text-danger">*</span></label>
                    <InputText
                        id="reason"
                        value={newCreditReason}
                        onChange={(e) => setNewCreditReason(e.target.value)}
                        placeholder="Enter reason for credit adjustment"
                        className="w-100"
                        maxLength={255}
                    />
                    <small className="text-muted">
                        Provide a clear reason for adding this credit (e.g., "Billing adjustment", "Goodwill credit", "Refund")
                    </small>
                </div>

                <div className="mt-3 p-2 bg-light-info rounded">
                    <p className="mb-0">
                        <i className="ti ti-info-circle me-1"></i>
                        A reference number will be automatically generated for this transaction.
                    </p>
                </div>
            </Dialog>
        );
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="/facility/utility_management/meter_management/power_meters">
                                        Power Meters
                                    </Link>
                                </li>
                                <li className="breadcrumb-item active">
                                    <span>View Meter</span>
                                </li>
                                <li className="breadcrumb-item active">
                                    <span>Prepaid Account</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <button
                                className="btn btn-link p-0 border-0"
                                onClick={handleNavigateBack}
                            >
                                <i className="ti ti-arrow-narrow-left"></i> Back
                            </button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="card">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Total Credits</h6>
                                        <h3 className="mb-0 text-success">KSh {Number(totalCredits).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                                        <small className="text-muted">{creditTotalRecords} transactions</small>
                                    </div>
                                    <i className="ti ti-arrow-down-left f-30 text-success"></i>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="card">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Total Debits</h6>
                                        <h3 className="mb-0 text-danger">KSh {Number(totalDebits).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                                        <small className="text-muted">{debitTotalRecords} transactions</small>
                                    </div>
                                    <i className="ti ti-arrow-up-right f-30 text-danger"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <div>
                                <h3>Power Customer Account Details</h3>
                            </div>
                        </div>

                        {loading && customerData.id === 'N/A' ? (
                            <div className="card-body">
                                <div className="d-flex justify-content-center">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="card-body p-0">
                                <div className="row g-0">
                                    {/* Customer Info Panel - Left Side */}
                                    <div className="col-md-3 border-end">
                                        <div className="p-3">
                                            <div className="d-flex align-items-center mb-4">
                                                <div className="avatar avatar-xl bg-light-warning rounded-circle me-3">
                                                    <i className="ti ti-bolt f-22"></i>
                                                </div>
                                                <div>
                                                    <h4 className="mb-1">{customerData.name}</h4>
                                                    <span className={`badge ${customerData.status === 'Active' ? 'bg-success text-light' : 'bg-danger text-light'}`}>
                                                        {customerData.status}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="list-group list-group-flush">
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Account Type</span>
                                                    <span className="fw-bold">{customerData.userType}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Account Number</span>
                                                    <span className="fw-bold">{customerData.accountNumber}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Device ID</span>
                                                    <span className="fw-bold">{customerData.deviceId}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Phone Number</span>
                                                    <span className="fw-bold">{customerData.phone}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Email</span>
                                                    <span className="fw-bold">{customerData.email}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Unit Name</span>
                                                    <span className="fw-bold">{customerData.unitName}</span>
                                                </div>
                                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                    <span className="text-muted">Last Updated</span>
                                                    <span className="fw-bold">{customerData.updatedAt}</span>
                                                </div>
                                            </div>

                                            <div className="mt-3 p-3 bg-light-warning rounded">
                                                <h6 className="mb-2">Account Balance</h6>
                                                {balanceLoading ? (
                                                    <div className="d-flex justify-content-center">
                                                        <div className="spinner-border spinner-border-sm text-warning" role="status">
                                                            <span className="visually-hidden">Loading balance...</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <h3 className="mb-0 text-warning">
                                                            KES {typeof prepaidBalance === "number"
                                                                ? prepaidBalance.toFixed(2)
                                                                : "0.00"}
                                                        </h3>
                                                        <small className="text-muted mt-1 d-block">Current Balance</small>
                                                    </div>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-outline-warning mt-3"
                                                    onClick={handleRefreshBalance}
                                                    disabled={balanceLoading}
                                                >
                                                    <i className="ti ti-refresh me-1"></i> Refresh Balance
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tabs Panel - Right Side */}
                                    <div className="col-md-9">
                                        <Toast ref={toast} />
                                        <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)} className="border-0">
                                            {/* Credits Tab */}
                                            <TabPanel header="Credits">
                                                <div className="p-3">
                                                    <div className="d-flex justify-content-end mb-3 gap-2">
                                                        <button
                                                            className="btn btn-outline-primary"
                                                            onClick={exportCreditsCSV}
                                                        >Export
                                                        </button>
                                                        {isNinja && (
                                                            <button
                                                                className="btn btn-outline-dark"
                                                                onClick={() => navigate(`/facility/utility_management/meter_management/power_meters/view_power_meter_tabs/power_credit_audit_log/${customerData.accountNumber}`)}
                                                            >Audit Log
                                                            </button>
                                                        )}
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={openAddDialog}
                                                        > Add New Credit
                                                        </button>
                                                    </div>
                                                    <div className="dt-responsive table-responsive">
                                                        <DataTable
                                                            value={credits}
                                                            emptyMessage="No credit transactions found."
                                                            sortMode="multiple"
                                                            paginator
                                                            lazy
                                                            first={creditLazyParams.first}
                                                            rows={creditLazyParams.rows}
                                                            totalRecords={creditTotalRecords}
                                                            onPage={onCreditPage}
                                                            loading={loading}
                                                            stripedRows
                                                            tableStyle={{ minWidth: '50rem' }}
                                                        >
                                                            <Column
                                                                field="ref"
                                                                header="Reference"
                                                                sortable
                                                                body={(rowData) => (
                                                                    <div>
                                                                        <div>{rowData.ref}</div>
                                                                        {rowData.type === 'Manual' && rowData.addedBy && (
                                                                            <small className="text-muted d-block">Added by {rowData.addedBy}</small>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            />
                                                            <Column
                                                                field="amount"
                                                                header="Amount"
                                                                body={amountBodyTemplate}
                                                                sortable
                                                            />
                                                            <Column field="time" header="Time" sortable />
                                                            <Column field="addedOn" header="Date Added" sortable />
                                                            <Column
                                                                field="type"
                                                                header="Payment Type"
                                                                sortable
                                                            />
                                                            <Column
                                                                field="reason"
                                                                header="Reason"
                                                                sortable
                                                                body={(rowData) => rowData.reason || 'N/A'}
                                                            />
                                                        </DataTable>
                                                    </div>
                                                </div>
                                            </TabPanel>

                                            {/* Debits Tab */}
                                            <TabPanel header="Debits">
                                                <div className="p-3">
                                                    <div className="d-flex justify-content-end mb-3">
                                                        <button
                                                            className="btn btn-outline-primary"
                                                            onClick={exportDebitsCSV}
                                                        > Export
                                                        </button>
                                                    </div>
                                                    <div className="dt-responsive table-responsive">
                                                        <DataTable
                                                            value={debits}
                                                            emptyMessage="No debit transactions found."
                                                            sortMode="multiple"
                                                            paginator
                                                            lazy
                                                            first={debitLazyParams.first}
                                                            rows={debitLazyParams.rows}
                                                            totalRecords={debitTotalRecords}
                                                            onPage={onDebitPage}
                                                            loading={loading}
                                                            stripedRows
                                                            tableStyle={{ minWidth: '50rem' }}
                                                        >
                                                            <Column
                                                                header="Readings"
                                                                body={readingsBodyTemplate}
                                                            />
                                                            <Column
                                                                field="consumption"
                                                                header="Consumption"
                                                                body={consumptionBodyTemplate}
                                                                sortable
                                                            />
                                                            <Column field="rate" header="Rate" sortable />
                                                            <Column
                                                                field="totalAmount"
                                                                header="Total Amount"
                                                                body={totalAmountBodyTemplate}
                                                                sortable
                                                            />
                                                            <Column field="date" header="Date" sortable />
                                                            <Column field="time" header="Time" sortable />
                                                        </DataTable>
                                                    </div>
                                                </div>
                                            </TabPanel>
                                        </TabView>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {renderAddCreditDialog()}
        </Layout>
    );
}

export default ViewPrepaidPowerCustomer;